import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { PasswordStrength, isStrongPassword } from "@/components/portfolio/PasswordStrength";
import { API, formatApiErrorDetail } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isStrongPassword(password)) {
      setError("Crie uma senha com pelo menos 8 caracteres, incluindo letras e números.");
      return;
    }
    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(formatApiErrorDetail(data.detail, "Erro ao redefinir"));
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-4 py-16 text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="gradient-ring w-full max-w-md rounded-2xl p-8 shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
      >
        <span className="font-display text-lg font-extrabold tracking-tight">
          LUIZ<span className="text-[#E50914]">.DEV</span>
        </span>

        {done ? (
          <div className="mt-8 text-center" data-testid="reset-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E50914]/10">
              <CheckCircle2 className="h-7 w-7 text-[#E50914]" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold">Senha redefinida!</h1>
            <p className="mt-3 text-sm text-zinc-400">Sua nova senha já está valendo. Entre com ela agora.</p>
            <a
              data-testid="reset-go-login"
              href="/entrar"
              className="btn-red-glow mt-8 inline-flex items-center justify-center rounded-full bg-[#E50914] px-8 py-3.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
            >
              Ir para o login
            </a>
          </div>
        ) : !token ? (
          <div className="mt-8" data-testid="reset-invalid">
            <h1 className="font-display text-2xl font-bold">Link inválido</h1>
            <p className="mt-3 text-sm text-zinc-400">
              Este link de redefinição não é válido. Solicite um novo na página de login.
            </p>
            <a href="/recuperar-senha" className="mt-6 inline-block text-sm text-[#E50914] hover:underline">
              Solicitar novo link
            </a>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-display text-2xl font-bold">Criar nova senha</h1>
            <p className="mt-2 text-sm text-zinc-500">Escolha uma senha forte: mínimo de 8 caracteres, com letras e números.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="relative">
                <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  maxLength={128}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Nova senha"
                  className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
                />
              </div>
              <PasswordStrength password={password} />
              <div className="relative">
                <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  maxLength={128}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repita a nova senha"
                  className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
                />
              </div>
              {error && (
                <div data-testid="reset-error" className="rounded-xl border border-[#E50914]/40 bg-[#E50914]/10 px-4 py-3 text-sm text-[#FF2A3B]">
                  {error}
                </div>
              )}
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="btn-red-glow flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#E50914] font-mono-code text-sm font-bold uppercase tracking-widest text-white disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar nova senha"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
